import { RowLayout } from '../../../Vendors/VendorCreation/General/VendorForm/RowLayout'
import { healthSystemsInitialValues } from '../../../../../components/Forms/constants'
import {
  formatPhoneNumber,
  formatPhoneNumberToRequest,
  getUserCommunity,
  validateMessages,
  websiteValidationRegex
} from '../../../../../helper/common'
import { Input } from '../../../../../components/Input'
import { BTN_TXT, LABELS, PLACEHOLDERS } from '../../../../../constants'
import { Select } from '../../../../../components/Select'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TOption } from '../../../../../components/Select/types'
import { Form, Radio } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import { fetchStateOptions } from '../../../../PaymentDetails/api'
import { setLoading } from '../../../../../redux/store/common/slice'
import {
  addHealthSystem,
  editHealthSystem,
  editHealthSystemOutsideCommunity
} from '../../../MyOrganizationCommunity/api'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from '../../../../../redux/store/user/getters'
import {
  TAddEditHealthSystemErrors,
  TAddEditHealthSystemForm,
  TAddEditHealthSystemData
} from '../types'
import { Checkbox } from '../../../../../components/Checkbox'
import { Typography } from '../../../../../components/Typography'
import { ability, ACTIONS, SUBJECTS } from '../../../../Permission'
import '../styles.scss'
import { FieldWithLabel } from '../../../../../components/FieldWithLabel/FieldWithLabel'
import { Button, BUTTON_TYPES } from '../../../../../components/Button'
import { ReactComponent as Plus16 } from '../../../../../assets/svg/Plus16.svg'
import { ReactComponent as Delete24 } from '../../../../../assets/svg/Delete16Red.svg'
import { FloatFooter } from '../../../../../components/FloatFooter/FloatFooter'
import { routes } from '../../../../../router'
import { useNavigate } from 'react-router'
import { getDefaultDepartmentsListRequest } from '../../../api'
import { useHSDetailsContext } from '../Providers/HSContextProvider'
import { AxiosResponse } from 'axios'
import { getGpoList } from '../../../Gpo/api'
import {
  updateGpoRelation,
  updateGpoRelationName
} from '../../ShortView/Details/api'
import { SaveChangesPopup } from './SaveChangesPopup'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ReactComponent as Information } from 'assets/svg/Information.svg'
import { COMMUNITY_ACCESS_TOOLTIP_VALUES } from '../../../MyOrganizationCommunity/HSDetails/constants'

export const CreateHSForm = () => {
  const [form] = Form.useForm<TAddEditHealthSystemForm>()
  const [stateList, setStateList] = useState<TOption[]>([])
  const [errors, setErrors] = useState<TAddEditHealthSystemErrors>({})
  const [defaultDepartmentsList, setDefaultDepartmentsList] = useState<
    string[]
  >([])
  const [gpoList, setGpoList] = useState<TOption[]>([])
  const [isActive, setIsActive] = useState(false)

  const [isConfirmedEditing, setIsConfirmedEditing] = useState(false)
  const [isEditingConfirmationOpen, setIsEditingConfirmationOpen] =
    useState(false)

  const {
    state: {
      details,
      communityAccessOptions,
      canEditCommunityAccess,
      outsideCommunityMode
    }
  } = useHSDetailsContext()

  const user = useSelector(getUser)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const canEditGPO =
    ability.can(ACTIONS.MANAGE, SUBJECTS.GPO_HS) &&
    details?.uuid === user.health_system

  const canSeeCommunityAccess =
    !!communityAccessOptions && canEditCommunityAccess && !outsideCommunityMode

  const phoneNumber = Form.useWatch('phone_number', form)
  const departments = Form.useWatch('departments', form)
  const isGPOSharedWithCommunity = Form.useWatch('gpo_relation_shared', form)

  const getDuplicatedDepartments = useMemo(() => {
    if (!departments) return new Set()
    const seen = new Map()
    const duplicates = new Set()

    departments.forEach((item, index) => {
      const key = item?.name?.trim().toLowerCase()
      if (!key) return
      if (
        defaultDepartmentsList.some((dep) => dep.trim().toLowerCase() === key)
      ) {
        duplicates.add(index)
      }
      if (seen.has(key)) {
        duplicates.add(index)
        duplicates.add(seen.get(key))
      } else {
        seen.set(key, index)
      }
    })
    return duplicates
  }, [departments, defaultDepartmentsList])

  const onFormChange = () => {
    setIsActive(true)
  }

  const predefineFormData = useCallback(() => {
    if (details) {
      form.setFieldsValue({
        name: details.name,
        legal_name: details.legal_name,
        website: details.website,
        phone_number: details.phone_number,
        state: details.address.state,
        street: details.address.street,
        zip_code: details.address.zip_code,
        city: details.address.city,
        gpo: details.gpo?.gpo?.uuid,
        community_access: details.community_access,
        departments: details.departments ?? [],
        gpo_relation_shared: details.gpo?.is_shared_with_community
      })
    }
  }, [details, form])

  useEffect(() => {
    predefineFormData()
  }, [predefineFormData])

  const onCancel = useCallback(() => {
    setIsActive(false)
    if (details) {
      predefineFormData()
    } else {
      form.resetFields()
    }
    setErrors({})
  }, [form, details, predefineFormData])

  const getStateList = useCallback(async () => {
    await fetchStateOptions().then((res) => {
      setStateList(
        res.data.results.map((option) => ({
          label: option.id,
          value: option.id
        }))
      )
    })
  }, [])

  const getDefaultDepartmentsList = useCallback(async () => {
    await getDefaultDepartmentsListRequest(getUserCommunity(user) ?? '').then(
      (res) => {
        setDefaultDepartmentsList(res.data.results.map((i) => i.name))
      }
    )
  }, [user])

  const fetchGpoList = async () => {
    await getGpoList({}).then((res) => {
      setGpoList(
        res.data.results.map((option) => ({
          label: option.name,
          value: option.uuid
        }))
      )
    })
  }

  useEffect(() => {
    getStateList()
    getDefaultDepartmentsList()
    if (canEditGPO) {
      fetchGpoList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateGPO = useCallback(
    async (uuid, value) => {
      if (
        !canEditGPO ||
        (details?.gpo?.gpo?.uuid === value &&
          isGPOSharedWithCommunity === details?.gpo?.is_shared_with_community)
      )
        return
      const newGPO =
        details?.gpo?.gpo?.uuid === value
          ? { data: details?.gpo }
          : await updateGpoRelationName(uuid, {
              uuid: value
            })
      if (!newGPO?.data?.is_same_gpo_for_community) return
      await updateGpoRelation(uuid, {
        is_shared_with_community: isGPOSharedWithCommunity
      })
    },
    [canEditGPO, isGPOSharedWithCommunity, details]
  )

  const handleAddEditHealthSystem = async (
    formData: TAddEditHealthSystemForm
  ) => {
    if (getDuplicatedDepartments.size > 0) {
      return
    }
    if (details?.uuid && !isConfirmedEditing) {
      setIsEditingConfirmationOpen(true)
      return
    }
    dispatch(setLoading(true))
    setIsActive(false)

    try {
      const healthSystemData: TAddEditHealthSystemData = {
        name: formData.name,
        legal_name: formData.legal_name,
        website: formData.website,
        phone_number: formatPhoneNumberToRequest(formData?.phone_number) ?? '',
        address: {
          state: formData.state,
          street: formData.street,
          zip_code: formData.zip_code,
          city: formData.city
        },
        status: undefined,
        departments: formData.departments,
        community_access: formData.community_access
      }
      if (details?.uuid) {
        const saveHealthSystem = outsideCommunityMode
          ? editHealthSystemOutsideCommunity(details.uuid, healthSystemData)
          : editHealthSystem(user.community, healthSystemData, details.uuid)
        await saveHealthSystem.then(() => updateGPO(details.uuid, formData.gpo))
      } else {
        await addHealthSystem(user.community, {
          ...healthSystemData,
          departments: healthSystemData.departments?.map((d) => d.name)
        }).then((resp) => updateGPO(resp.data.uuid, formData.gpo))
      }
      navigate(`${routes.myCommunity}${routes.myOrganization}/health_systems`, {
        replace: true
      })
    } catch (err) {
      const e = err as AxiosResponse
      setIsActive(true)
      setErrors(e.data)
    } finally {
      dispatch(setLoading(false))
    }
  }

  const onConfirmSaving = useCallback(() => {
    setIsConfirmedEditing(true)
    setIsEditingConfirmationOpen(false)
    form.submit()
  }, [form])

  const onCancelSaving = useCallback(() => {
    setIsEditingConfirmationOpen(false)
  }, [])

  const departmentsRemoved = useMemo(() => {
    if (details?.uuid) {
      // check if already saved department (with uuid) was removed
      return (
        departments?.filter((dep) => dep?.uuid).length !==
        details.departments?.length
      )
    }
    return false
  }, [details, departments])

  return (
    <>
      <div className="my-organization__create-hs">
        <CustomForm<TAddEditHealthSystemForm>
          form={form}
          onFinish={handleAddEditHealthSystem}
          initialValues={healthSystemsInitialValues}
          validateMessages={validateMessages}
          id="my-organization-create-hs-form"
          className="my-organization__create-hs__form"
          onChange={onFormChange}
        >
          <RowLayout title="Business information">
            <div className="row">
              <Input
                propsItem={{
                  name: 'name',
                  label: LABELS.HS_NAME_DOING_BUSINESS_AS,
                  help: errors.name?.[0],
                  validateStatus: errors.name && 'error',
                  rules: [
                    {
                      required: true
                    }
                  ]
                }}
                propsInput={{
                  showCount: true,
                  maxLength: 64
                }}
              />
            </div>
            <div className="row">
              <Input
                propsItem={{
                  name: 'legal_name',
                  label: LABELS.HS_LEGAL_NAME,
                  help: errors.legal_name?.[0],
                  validateStatus: errors.legal_name && 'error'
                }}
                propsInput={{
                  showCount: true,
                  maxLength: 64
                }}
              />
            </div>
            <div className="row two-columns">
              <Input
                propsInput={{ placeholder: PLACEHOLDERS.WEBSITE }}
                propsItem={{
                  name: 'website',
                  label: LABELS.WEBSITE,
                  help: errors.website?.[0],
                  validateStatus: errors.website && 'error',
                  rules: [
                    {
                      pattern: websiteValidationRegex,
                      validateTrigger: ['onSubmit']
                    }
                  ]
                }}
              />
              <Input.Phone
                propsItem={{
                  name: 'phone_number',
                  label: LABELS.PHONE_NUMBER
                }}
                propsInputNumber={{
                  controls: false,
                  formatter: formatPhoneNumber,
                  prefix: phoneNumber ? '+1' : ''
                }}
              />
            </div>
          </RowLayout>
          <div className="vendor-details-form__divider" />
          <RowLayout title="Address">
            <div className="row">
              <Input
                propsItem={{
                  name: 'street',
                  label: LABELS.STREET,
                  rules: [
                    {
                      required: true
                    }
                  ]
                }}
                propsInput={{
                  maxLength: 100
                }}
              />
            </div>
            <div className="row gap-24">
              <Input
                propsItem={{
                  name: 'city',
                  label: LABELS.CITY,
                  rules: [
                    {
                      required: true
                    }
                  ]
                }}
                propsInput={{
                  className: 'my-organization__create-hs__form__city'
                }}
              />
              <Select
                options={stateList}
                propsItem={{
                  label: LABELS.STATE,
                  name: 'state',
                  help: errors.address?.state?.[0],
                  validateStatus: errors.address?.state && 'error',
                  rules: [
                    {
                      required: true
                    }
                  ]
                }}
                propsSelect={{
                  required: true,
                  placeholder: PLACEHOLDERS.PLEASE_SELECT,
                  className: 'my-organization__create-hs__form__state',
                  onChange: onFormChange
                }}
              />
              <Input
                className="column-half"
                propsItem={{
                  name: 'zip_code',
                  label: LABELS.ZIP_CODE,
                  help: errors.address?.zip_code?.[0],
                  validateStatus: errors.address?.zip_code && 'error',
                  rules: [
                    {
                      required: true
                    }
                  ]
                }}
                propsInput={{
                  className: 'my-organization__create-hs__form__zip'
                }}
              />
            </div>
          </RowLayout>
          <div className="vendor-details-form__divider" />
          <RowLayout title="Departments">
            <div className="column gap-24">
              {defaultDepartmentsList.length > 0 && (
                <FieldWithLabel title={LABELS.DEFAULT_DEPARTMENTS}>
                  {defaultDepartmentsList.join(', ')}
                </FieldWithLabel>
              )}
              {
                <Form.List name="departments">
                  {(fields, { add, remove }) => (
                    <>
                      {departments?.length > 0 && (
                        <div className="my-organization__create-hs__form__department-names">
                          {fields.map(({ key, name }, id) => {
                            const isDuplicate = getDuplicatedDepartments.has(id)
                            return (
                              <div
                                key={key}
                                className="row gap-8 space-between my-organization__create-hs__form__department-name-item"
                              >
                                <Input
                                  propsItem={{
                                    name: [name, 'name'],
                                    validateStatus:
                                      (isDuplicate ||
                                        errors?.departments?.[name]?.name) &&
                                      'error',
                                    help: isDuplicate
                                      ? 'Duplicate departments are not allowed'
                                      : errors?.departments?.[name]?.name,
                                    rules: [
                                      {
                                        max: 100
                                      },
                                      {
                                        required: true,
                                        message: 'Department name is required'
                                      }
                                    ]
                                  }}
                                  propsInput={{
                                    maxLength: 100,
                                    className: 'full-width'
                                  }}
                                />
                                <Button.Icon
                                  className="user-form_delete-btn"
                                  type={BUTTON_TYPES.LINK}
                                  icon={<Delete24 />}
                                  onClick={() => {
                                    remove(name)
                                    setIsActive(true)
                                  }}
                                  danger
                                />
                              </div>
                            )
                          })}
                        </div>
                      )}
                      <Button
                        type={BUTTON_TYPES.GHOST}
                        icon={<Plus16 />}
                        className="user-form_add-hospital"
                        onClick={() => add()}
                        upperCase
                      >
                        {BTN_TXT.ADD_DEPARTMENT}
                      </Button>
                    </>
                  )}
                </Form.List>
              }
            </div>
          </RowLayout>
          {canSeeCommunityAccess && (
            <>
              <div className="vendor-details-form__divider" />
              <RowLayout title="Community access">
                <div className="row">
                  <Form.Item name="community_access">
                    <Radio.Group>
                      {communityAccessOptions?.map((option) => (
                        <Radio key={option.id} value={option.id}>
                          <div className="row align-center gap-8 mb-8">
                            <Typography.Body1>{option.value}</Typography.Body1>
                            {COMMUNITY_ACCESS_TOOLTIP_VALUES[option.id] && (
                              <Tooltip
                                title={
                                  COMMUNITY_ACCESS_TOOLTIP_VALUES[option.id]
                                }
                              >
                                <Information />
                              </Tooltip>
                            )}
                          </div>
                        </Radio>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </div>
              </RowLayout>
            </>
          )}
          {canEditGPO && (
            <>
              <div className="vendor-details-form__divider" />
              <RowLayout
                title="Group purchasing organization"
                subtitle="GPO selection is not required. Leave blank if none applies."
              >
                <div className="row">
                  <Select
                    options={gpoList}
                    propsItem={{
                      label: LABELS.GPO_NAME,
                      name: 'gpo'
                    }}
                    propsSelect={{
                      placeholder: PLACEHOLDERS.PLEASE_SELECT,
                      allowClear: true,
                      onChange: onFormChange
                    }}
                  />
                </div>
                {!outsideCommunityMode && (
                  <div className="row">
                    <Checkbox
                      propsItem={{
                        name: 'gpo_relation_shared',
                        valuePropName: 'checked'
                      }}
                      propsCheckbox={{
                        disabled: !details?.gpo?.is_same_gpo_for_community
                      }}
                    >
                      <Typography.Body1>
                        Share GPO admin fee with Community President
                      </Typography.Body1>
                    </Checkbox>
                  </div>
                )}
              </RowLayout>
            </>
          )}
        </CustomForm>
        <FloatFooter
          isActive={isActive}
          onCancel={onCancel}
          onSave={() => form.submit()}
        />
      </div>
      <SaveChangesPopup
        hsName={details?.name ?? ''}
        open={isEditingConfirmationOpen}
        onCancel={onCancelSaving}
        onSave={onConfirmSaving}
        departmentsRemoved={departmentsRemoved}
      />
    </>
  )
}
